import * as React from 'react';
import {
    ShimmerElementType,
    Shimmer,
    IShimmerStyles,
    IShimmerProps,
} from '@fluentui/react/lib/Shimmer';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';

const styles = require('./TodoListShimmer.scss');

const DEFAULT_WIDTH = 251;

export interface TodoListShimmerProps {
    isDataLoaded: boolean;
}
const numShimmerElements = 8;

const circleWidth = 20;
const gapWidth = 15;

export const TodoListShimmer: React.FunctionComponent<TodoListShimmerProps> = props => {
    const ref = React.useRef<HTMLDivElement>();
    const [rect] = useElementSizeTracker(ref);

    const totalWidth = rect?.width || DEFAULT_WIDTH;
    const shimmerElements: JSX.Element[] = React.useMemo(() => renderShimmerItems(totalWidth), [
        totalWidth,
    ]);

    /** The parent shimmer component below manages the transition from the Shimmer view to the props.children,
     * because of this, we set the shimmer colors to none. ( The child shimmer components rendered via customElementsGroup
     * contain the viewable part of the shimmer)
     */
    return (
        <div ref={ref} className={styles.wrapper}>
            <Shimmer
                styles={getShimmerContainerStyles}
                shimmerColors={{ shimmer: 'none', shimmerWave: 'none' }}
                isDataLoaded={props.isDataLoaded}
                customElementsGroup={shimmerElements}>
                {props.children}
            </Shimmer>
        </div>
    );
};

/**
 * Renders shimmer elements. For each shimmer element, circle represents the completion checkbox, gap represents spacing
 * after the completion checkbox, and line represents the task subject and details.
 * @param totalWidth the width of the entire item
 */
function renderShimmerItems(totalWidth?: number): JSX.Element[] {
    let shimmerElements: JSX.Element[] = [];
    for (let i = 0; i < numShimmerElements; i++) {
        shimmerElements.push(
            <div className={styles.shimmerItemWrapper} key={'todoListShimmer_' + i}>
                <Shimmer
                    styles={getShimmerStyles}
                    shimmerElements={[
                        { type: ShimmerElementType.circle, height: circleWidth },
                        { type: ShimmerElementType.gap, width: gapWidth },
                        {
                            type: ShimmerElementType.line,
                            width: totalWidth - circleWidth - gapWidth,
                            height: 10,
                        },
                    ]}
                />
            </div>
        );
    }
    return shimmerElements;
}

function getShimmerStyles(props: IShimmerProps): IShimmerStyles {
    return {
        root: {
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 19px',
        },
    };
}

function getShimmerContainerStyles(props: IShimmerProps): IShimmerStyles {
    return {
        root: {
            minHeight: '0',
            height: '100%', // required for IE11
            display: 'flex',
            flex: '1 1 auto',
        },
        dataWrapper: {
            height: 'auto',
            display: 'flex',
            flex: '1 1 auto',
            width: '100%',
        },
    };
}
